import { useEffect } from "react";

const C = {
  bg: "#FAF7F2",
  white: "#FFFFFF",
  orange: "#E87C0C",
  dark: "#1A1208",
  charcoal: "#2D2416",
  muted: "#5C4D3C",
  border: "#EDE5D8",
};

const sections = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    content: [
      { subtitle: "Personal Information", text: "When you register and use SwaadIndia, we collect your full name and phone number to create and manage your account." },
      { subtitle: "Location Data", text: "We collect your delivery address and approximate location to show you nearby restaurants, calculate delivery estimates, and route your orders accurately. Location is only accessed when you use the app." },
      { subtitle: "Order History", text: "We store records of your past food orders including items, quantities, prices, and delivery details to help you reorder easily and resolve any disputes." },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    items: [
      "To create and manage your SwaadIndia account",
      "To process and deliver your food orders",
      "To send order confirmations and delivery updates via WhatsApp or SMS",
      "To show relevant restaurants and menus based on your location",
      "To provide customer support and resolve issues",
      "To improve app performance and user experience",
      "To comply with legal obligations",
    ],
  },
  {
    id: "third-party",
    title: "Third-Party Services",
    services: [
      { name: "Razorpay", purpose: "Payment processing", detail: "We use Razorpay to securely handle all payment transactions. Your payment card details are never stored on our servers — they are processed directly by Razorpay under PCI-DSS compliance.", link: "https://razorpay.com/privacy/" },
      { name: "Firebase (Google)", purpose: "Authentication, database & notifications", detail: "We use Firebase for user authentication, storing app data, and sending push notifications. Firebase is a Google service governed by Google's privacy policies.", link: "https://firebase.google.com/support/privacy" },
      { name: "Google Maps", purpose: "Location & delivery mapping", detail: "We use Google Maps to display restaurant locations, calculate delivery routes, and let you pin your delivery address.", link: "https://policies.google.com/privacy" },
      { name: "WhatsApp / Twilio", purpose: "OTP & SMS notifications", detail: "We use WhatsApp or Twilio to send one-time passwords for phone verification and order status updates via SMS.", link: "https://www.twilio.com/en-us/legal/privacy" },
    ],
  },
  {
    id: "data-sharing",
    title: "Data Sharing",
    content: [
      { text: "We do not sell, rent, or trade your personal information to any third party for marketing purposes." },
      { text: "We share your information only in the following situations:" },
    ],
    items: [
      "With restaurant partners to fulfill your order (name, delivery address, order details)",
      "With delivery partners to complete delivery (name, phone, address)",
      "With payment processors (Razorpay) to handle transactions",
      "With service providers listed above to operate the app",
      "When required by law or to protect rights and safety",
    ],
  },
  {
    id: "data-retention",
    title: "Data Retention & Deletion",
    content: [
      { text: "We retain your personal data as long as your account is active or as needed to provide our services." },
      { text: "You can request deletion of your account and personal data at any time by contacting us. We will process your request within 30 days." },
    ],
  },
  {
    id: "security",
    title: "Data Security",
    content: [
      { text: "We implement industry-standard security measures including encrypted data transmission (HTTPS/TLS), Firebase security rules, and restricted access controls to protect your personal information." },
      { text: "However, no method of transmission over the internet is 100% secure. We encourage you to keep your account credentials confidential." },
    ],
  },
  {
    id: "childrens",
    title: "Children's Privacy",
    content: [
      { text: "SwaadIndia is intended for users aged 18 and above. We do not knowingly collect personal information from anyone under 18. If we become aware that a minor has provided us with personal data, we will delete it promptly." },
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights",
    items: [
      "Access the personal data we hold about you",
      "Request correction of inaccurate data",
      "Request deletion of your data",
      "Withdraw consent for location access via your device settings",
      "Opt out of promotional communications",
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    content: [
      { text: "We may update this Privacy Policy from time to time. When we do, we will revise the 'Last Updated' date at the top of this page. Continued use of the app after changes constitutes acceptance of the updated policy." },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    content: [{ text: "If you have any questions or requests regarding this Privacy Policy, please contact us:" }],
    contact: {
      name: "SwaadIndia Support",
      email: "swaadindia.app@gmail.com",
      phone: "7030078393 / 7276857746",
      address: "Punawale, Pune 411033",
      website: "https://swaadindia.co.in",
    },
  },
];

export default function PrivacyPolicy({ onBack }) {
  useEffect(() => {
    document.title = "Privacy Policy — SwaadIndia";
    window.scrollTo(0, 0);
    return () => { document.title = "SwaadIndia — Mess Management Platform"; };
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div style={{ fontFamily: "'Jost', sans-serif", background: C.bg, color: C.dark, minHeight: "100vh" }}>

      {/* Header */}
      <header style={{ background: C.white, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 100, padding: "0 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/swaadIndia_logo.png" alt="SwaadIndia" style={{ height: 40, width: "auto" }} />
            <span style={{ background: "#FEF3E2", color: C.orange, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.5px" }}>Privacy Policy</span>
          </div>
          {onBack && (
            <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 18px", fontSize: 14, color: C.muted, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
              &#8592; Back to Site
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${C.orange} 0%, #c96a08 100%)`, color: "#fff", padding: "60px 24px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.8, marginBottom: 12 }}>Legal &middot; Privacy</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 700, margin: "0 0 16px", lineHeight: 1.1 }}>Your Privacy Matters</h1>
          <p style={{ fontSize: 16, opacity: 0.9, maxWidth: 520, margin: "0 0 20px", lineHeight: 1.7 }}>This policy explains what data SwaadIndia collects, how we use it, and the choices you have. Plain language — no legal jargon.</p>
          <span style={{ fontSize: 13, opacity: 0.8, background: "rgba(255,255,255,0.15)", display: "inline-block", padding: "6px 14px", borderRadius: 6 }}>Last Updated: <strong>June 17, 2026</strong></span>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 24px", display: "grid", gridTemplateColumns: "220px 1fr", gap: 48, alignItems: "start" }}>

        {/* TOC */}
        <aside style={{ position: "sticky", top: 84, background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px 16px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: C.muted, marginBottom: 14 }}>On this page</p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {sections.map(s => (
              <li key={s.id}>
                <button onClick={() => scrollTo(s.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.muted, padding: "6px 0", display: "block", textAlign: "left", width: "100%", lineHeight: 1.4, fontFamily: "inherit" }}>
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Content */}
        <article>
          {/* Intro */}
          <div style={{ background: "#FEF3E2", border: `1px solid #f5d5a0`, borderLeft: `4px solid ${C.orange}`, borderRadius: 10, padding: "18px 20px", marginBottom: 40 }}>
            <p style={{ fontSize: 15, color: C.muted, margin: 0, lineHeight: 1.75 }}>SwaadIndia ("we", "our", or "us") operates the SwaadIndia mobile application. By using our app, you agree to the collection and use of information as described in this policy. This policy applies to all users aged 18 and above.</p>
          </div>

          {sections.map(section => (
            <section key={section.id} id={section.id} style={{ paddingBottom: 40, marginBottom: 40, borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: C.dark, marginBottom: 16 }}>{section.title}</h2>

              {section.content?.map((item, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  {item.subtitle && <h3 style={{ fontSize: 15, fontWeight: 600, color: C.orange, marginBottom: 6 }}>{item.subtitle}</h3>}
                  <p style={{ fontSize: 15, color: C.muted, margin: 0, lineHeight: 1.75 }}>{item.text}</p>
                </div>
              ))}

              {section.items && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {section.items.map((item, i) => (
                    <li key={i} style={{ display: "flex", gap: 10, fontSize: 15, color: C.muted, padding: "8px 0", borderBottom: `1px solid ${C.border}`, lineHeight: 1.6 }}>
                      <span style={{ color: C.orange, fontWeight: 700, flexShrink: 0 }}>&#10003;</span>{item}
                    </li>
                  ))}
                </ul>
              )}

              {section.services && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
                  {section.services.map(svc => (
                    <div key={svc.name} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: C.dark }}>{svc.name}</span>
                        <span style={{ fontSize: 12, color: C.muted, background: "#FEF3E2", padding: "2px 8px", borderRadius: 20 }}>{svc.purpose}</span>
                      </div>
                      <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, margin: "0 0 10px" }}>{svc.detail}</p>
                      <a href={svc.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.orange, textDecoration: "none", fontWeight: 500 }}>View privacy policy &#8594;</a>
                    </div>
                  ))}
                </div>
              )}

              {section.contact && (
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px", marginTop: 16 }}>
                  {[
                    { label: "App", value: section.contact.name },
                    { label: "Email", value: section.contact.email, href: `mailto:${section.contact.email}` },
                    { label: "Phone", value: section.contact.phone },
                    { label: "Address", value: section.contact.address },
                    { label: "Website", value: section.contact.website, href: section.contact.website },
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", gap: 16, fontSize: 15, color: C.muted, marginBottom: 12, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: C.muted, minWidth: 64 }}>{row.label}</span>
                      {row.href
                        ? <a href={row.href} style={{ color: C.orange, textDecoration: "none", fontWeight: 500 }}>{row.value}</a>
                        : <span>{row.value}</span>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </div>

      {/* Footer */}
      <div style={{ background: C.charcoal, padding: "32px 24px", textAlign: "center" }}>
        <p style={{ color: "#9a8070", fontSize: 13, margin: "0 0 12px" }}>&#169; {new Date().getFullYear()} SwaadIndia &middot; Premontix. All rights reserved.</p>
        {onBack && (
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "8px 20px", fontSize: 13, color: "#c8b4a0", cursor: "pointer", fontFamily: "inherit" }}>
            &#8592; Back to SwaadIndia
          </button>
        )}
      </div>
    </div>
  );
}
